import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Clock, Users, Calculator, DollarSign } from "lucide-react";

const StaffHoursTracker = ({ taskId, staffHours = [], totalCost = 0, onUpdate, canEdit = false, hideCosts = false }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [hours, setHours] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (canEdit) {
      fetchEmployees();
    }
  }, [canEdit]);

  const fetchEmployees = async () => {
    try {
      // Names-only directory, not /employees — that one carries salaries and
      // bonus rates and 403s for roles that may still log hours (art director).
      const res = await api.get("/employees/directory");
      setEmployees((res.data || []).filter((e) => e.salaried));
    } catch (error) {
      console.error("Failed to load employees:", error);
    }
  };

  const handleAddHours = async () => {
    if (!selectedEmployee || !hours || parseFloat(hours) <= 0) {
      toast.error("Please select employee and enter valid hours");
      return;
    }

    setSaving(true);
    try {
      await api.post(`/tasks/${taskId}/staff-hours`, {
        employee_id: selectedEmployee,
        hours: parseFloat(hours),
      });
      toast.success("Staff hours added");
      setDialogOpen(false);
      setSelectedEmployee("");
      setHours("");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add staff hours");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await api.delete(`/tasks/${taskId}/staff-hours/${entryId}`);
      toast.success("Entry removed");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Failed to remove entry");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "UAH",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
          <Users className="h-4 w-4 text-blue-400" />
          Staff Hours & Cost
        </CardTitle>
        {canEdit && (
          <Button
            size="sm"
            onClick={() => setDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 h-8"
            data-testid="add-staff-hours-btn"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Hours
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {staffHours.length === 0 ? (
          <div className="text-center py-6 text-zinc-500">
            <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No staff hours recorded</p>
            {canEdit && <p className="text-xs mt-1">Click "Add Hours" to track team time</p>}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400 text-xs">Employee</TableHead>
                  <TableHead className="text-zinc-400 text-xs text-center">Hours</TableHead>
                  {!hideCosts && <TableHead className="text-zinc-400 text-xs text-right">Rate/h</TableHead>}
                  {!hideCosts && <TableHead className="text-zinc-400 text-xs text-right">Cost</TableHead>}
                  {canEdit && <TableHead className="text-zinc-400 text-xs w-10"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {staffHours.map((entry) => (
                  <TableRow key={entry.id} className="border-zinc-800 hover:bg-zinc-800/50">
                    <TableCell>
                      <div>
                        <span className="text-zinc-50 text-sm">{entry.employee_name}</span>
                        <div className="text-xs text-zinc-500">
                          Added by {entry.added_by_name}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-zinc-50 font-mono text-sm">{entry.hours}h</span>
                    </TableCell>
                    {!hideCosts && (
                      <TableCell className="text-right">
                        <span className="text-zinc-400 text-xs font-mono">
                          {formatCurrency(entry.hourly_rate)}
                        </span>
                      </TableCell>
                    )}
                    {!hideCosts && (
                      <TableCell className="text-right">
                        <span className="text-green-400 font-mono text-sm">
                          {formatCurrency(entry.cost)}
                        </span>
                      </TableCell>
                    )}
                    {canEdit && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="h-7 w-7 text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                          data-testid={`delete-hours-${entry.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Total - hidden for partners */}
            {!hideCosts && (
              <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-2 text-zinc-400">
                  <Calculator className="h-4 w-4" />
                  <span className="text-sm">Total Staff Cost:</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-green-400" />
                  <span className="text-lg font-bold text-green-400 font-mono">
                    {formatCurrency(totalCost)}
                  </span>
                </div>
              </div>
            )}
            {/* Total hours only for partners */}
            {hideCosts && (
              <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-2 text-zinc-400">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">Total Hours:</span>
                </div>
                <span className="text-lg font-bold text-zinc-50 font-mono">
                  {staffHours.reduce((sum, entry) => sum + (entry.hours || 0), 0)}h
                </span>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Add Hours Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-400" />
              Add Staff Hours
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Employee</Label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50" data-testid="select-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} ({emp.position || "Staff"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500">
                Cost is calculated from employee salary / working hours in month
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Hours Worked</Label>
              <Input
                type="number"
                step="0.5"
                min="0.5"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="e.g. 4.5"
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
                data-testid="hours-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleAddHours}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-hours-btn"
            >
              {saving ? "Saving..." : "Add Hours"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default StaffHoursTracker;
